import { useEffect, useState } from "react";
import {
  createSchedule,
  deleteSchedule,
  listSchedules,
} from "../lib/resources.ts";
import type { Schedule } from "../lib/types.ts";
import ScheduleEditor from "./ScheduleEditor.tsx";

// Schedules (admin-only) — the publish gate. Decide what plays when and where.
export default function Schedules() {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Set when we just created the schedule, so the editor focuses the name field. */
  const [isNew, setIsNew] = useState(false);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      setSchedules(await listSchedules());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  /** Opens the new schedule immediately; naming happens in the editor. */
  async function create() {
    setCreating(true);
    setError(null);
    try {
      const s = await createSchedule("Untitled schedule");
      await refresh();
      setIsNew(true);
      setOpenId(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn’t create the schedule");
    } finally {
      setCreating(false);
    }
  }

  async function remove(s: Schedule) {
    if (!confirm(`Delete schedule “${s.name}”? Displays using it will fall back to their default playlist.`))
      return;
    await deleteSchedule(s.id);
    await refresh();
  }

  if (openId) {
    return (
      <ScheduleEditor
        scheduleId={openId}
        autoFocusName={isNew}
        onClose={() => {
          setOpenId(null);
          setIsNew(false);
          void refresh();
        }}
      />
    );
  }

  return (
    <section className="admin-page">
      <div className="page-head">
        <div>
          <h1>Schedules</h1>
          <p className="muted">
            Drop content and playlists onto a schedule with timing and priority,
            then assign it to displays. Scheduling is the publish gate.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={create} disabled={creating}>
            {creating ? "Creating…" : "+ Create schedule"}
          </button>
        </div>
      </div>

      {error && <div className="banner banner--error">{error}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : schedules.length === 0 ? (
        <div className="admin-empty">
          <strong>No schedules yet</strong>
          <span>
            Hit “Create schedule” — you can name it on the next screen, then add
            timed entries and assign it to a display.
          </span>
        </div>
      ) : (
        <div className="list">
          {schedules.map((s) => (
            <div className="list-row card" key={s.id}>
              <button className="list-row-main" onClick={() => setOpenId(s.id)}>
                <span className="list-row-name">{s.name}</span>
                <span className="muted">
                  {s.entryCount} {s.entryCount === 1 ? "entry" : "entries"}
                </span>
              </button>
              <div className="list-row-actions">
                <button className="link-btn" onClick={() => setOpenId(s.id)}>
                  Edit
                </button>
                <button className="link-btn link-btn--danger" onClick={() => remove(s)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
